import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {toast} from 'react-toastify';

import Button from '../../../../../components/button';
import CustomCheckbox from '../../../../../components/mazraeKit/customCheckbox';
import CustomHeader from '../../../../../components/mazraeKit/headers/homeHeader';
import NoItemFoundPage from '../../../../../components/noItemFoundPage';
import SearchField from '../../../../../components/SearchField';
import SpinnerLoading from '../../../../../components/SpinnerLoading';

import useGetMyStoreAds from '../../../../../services/hookService/getMyStoreAds/useGetMyStoreAds';
import usePatchBulkStoreAdvertisement from '../../../../../services/hookService/patchBulkStoreAdvertisement/usePatchBulkStoreAdvertisement';

import useDebounce from '../../../../../utilities/hooks/useDebounce/useDebounce';

import DefaultImage from '../../../../../assets/image/newShop/defaultImg.png';

import roundLastThreeToZero from '../../../../../utilities/helper/unitConvertor/roundLastThreeToZero';
import AdvertisementBulkSelectableCard from './components/AdvertisementBulkSelectableCard';
import FilterAdsByInputGroupBottomSheet from './components/FilterAdsByInputGroupBottomSheet';
import IconActionButton from './components/IconActionButton';
import {discountedAdvertisementPrice} from './helper/discountedAdvertisementPrice';

const EditBulkStoreAdsPage = () => {
  const navigate = useNavigate();

  const [percentage, setPercentage] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);

  const [filterAdType, setFilterAdType] = useState<string[]>([]);
  const [openFilterBottomSheet, setOpenFilterBottomSheet] = useState(false);

  const [selectedAdIds, setSelectedAdIds] = useState<Set<string>>(
    () => new Set(),
  );

  const {data: storeAds, isLoading} = useGetMyStoreAds({
    search: debouncedSearch,
    advertisement_type: filterAdType,
    status: 'published',
    has_price: true,
  });

  const allIds = useMemo(() => storeAds?.map(ad => ad.id) ?? [], [storeAds]);

  const isAllSelected =
    allIds.length > 0 && selectedAdIds.size === allIds.length;

  const toggleAdSelection = (id: string) => {
    setSelectedAdIds(prev => {
      const next = new Set(prev);

      next.has(id) ? next.delete(id) : next.add(id);

      return next;
    });
  };

  const toggleSelectAllAds = () => {
    if (!storeAds) return;

    setSelectedAdIds(prev => {
      if (prev.size === storeAds.length) {
        return new Set(); // unselect all
      }

      return new Set(storeAds.map(ad => ad.id)); // select all
    });
  };

  // Reset selection when filters change
  useEffect(() => {
    setSelectedAdIds(new Set());
  }, [filterAdType, debouncedSearch]);

  // Grouping (UI order improvement)
  const {selectedCards, unselectedCards} = useMemo(() => {
    if (!storeAds) return {selectedCards: [], unselectedCards: []};

    return {
      selectedCards: storeAds.filter(ad => selectedAdIds.has(ad.id)),
      unselectedCards: storeAds.filter(ad => !selectedAdIds.has(ad.id)),
    };
  }, [storeAds, selectedAdIds]);

  const {mutate: bulkEdit, isLoading: isBulkEditing} =
    usePatchBulkStoreAdvertisement(() => {
      toast.success('تغییرات با موفقیت ثبت شد.');
      navigate('/manage-my-store-ads?tab=published');
    });

  const applyBulkEdit = () => {
    if (!storeAds) return;

    const value = Number(percentage);

    if (!value) return;

    const payload = storeAds
      .filter(ad => selectedAdIds.has(ad.id))
      .map(ad => {
        // current visible price after discount
        const currentVisiblePrice = discountedAdvertisementPrice(
          ad.ad_price,
          ad.discount_amount,
        );

        // apply percentage increase to visible price
        const increasedVisiblePrice =
          currentVisiblePrice + currentVisiblePrice * (value / 100);

        let finalPrice = increasedVisiblePrice;

        // reverse discount to get original ad_price
        if (ad.discount_amount) {
          finalPrice =
            (100 * increasedVisiblePrice) / (100 - ad.discount_amount);
        }

        return {
          id: ad.id,
          ad_price: roundLastThreeToZero(Math.round(finalPrice)),
        };
      });

    bulkEdit(payload);
  };

  const isBulkEditDisabled = selectedAdIds.size === 0 || !Number(percentage);

  const onPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');

    if ((value.match(/\./g)?.length ?? 0) > 1) return;

    setPercentage(value);
  };

  const selectedAdsData = useMemo(() => {
    if (!storeAds) return [];

    return storeAds
      .filter(ad => selectedAdIds.has(ad.id))
      .map(ad => ({
        id: ad.id,
        title: ad.title,
        original_price: ad.ad_price,
        ad_price: discountedAdvertisementPrice(ad.ad_price, ad.discount_amount), // applied discount price
        discount_amount: ad.discount_amount ?? 0,
        image:
          ad?.main_image?.image ??
          ad?.main_image?.image_128 ??
          ad?.sub_brand_main_image?.image ??
          DefaultImage,
      }));
  }, [storeAds, selectedAdIds]);

  return (
    <div className="flex flex-col h-full">
      <CustomHeader
        title="ویرایش چند آگهی"
        preComponent={() => <div />}
        postComponent={() => (
          <i
            className="text-2xl icon-nameClose-remove"
            onClick={() => navigate(-1)}
          />
        )}
      />

      <section className="px-6 pb-8">
        <div className="mt-4">
          <label htmlFor="percentage" className="text-sm text-zinc-500 ">
            درصد افزایش قیمت <span className="text-red-600">*</span>
          </label>

          <div className="relative">
            <input
              id={'percentage'}
              name={'percentage'}
              type={'text'}
              inputMode="numeric"
              value={percentage}
              placeholder={'درصد افزایش قیمت را وارد کنید'}
              onChange={onPercentageChange}
              className={
                'w-full px-3 border-[1px] text-sm mt-2 border-gray-500 rounded-lg transition-colors duration-150 focus:outline-none placeholder:text-sm placeholder:text-gray-400 h-10'
              }
            />

            {percentage && (
              <span className="absolute text-sm font-medium text-gray-400 top-5 left-4">
                درصد
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="flex flex-col px-6 pb-3">
        <div className="">
          <p className="m-0 text-sm text-Medium text-zinc-500">
            انتخاب آگهی <span className="text-red-600">*</span>
          </p>

          <span className="mt-3 text-[13px] tracking-[1rem] text-gray-500">
            آگهی‌هایی را که می‌خواهید تغییرات در آن‌ها اعمال شود انتخاب کنید.
          </span>
        </div>

        <div className="flex items-center justify-between mt-2">
          <div className="w-[65%]">
            <SearchField
              placeholder="جستجو در آگهی‌ها"
              value={search}
              onChange={setSearch}
            />
          </div>

          <IconActionButton
            title="فیلتر کردن"
            onClick={() =>
              (storeAds?.length! > 0 || filterAdType?.length > 0) &&
              setOpenFilterBottomSheet(true)
            }
            icon="icon-nameFilter"
            isActive={Boolean(filterAdType?.length !== 0)}
          />
        </div>

        <CustomCheckbox
          name="select_all_ads"
          customClass="pb-2 pt-3 gap-2 !text-gray-600"
          title="انتخاب همه آگهی ها"
          onChange={toggleSelectAllAds}
          checked={isAllSelected}
        />
      </section>

      <div className="flex-1 px-6 pb-24 overflow-y-auto">
        {!isLoading && !storeAds?.length && (
          <NoItemFoundPage className="h-[30dvh]" />
        )}

        {isLoading && (
          <div className="flex justify-center mt-8">
            <SpinnerLoading size={45} />
          </div>
        )}

        {!isLoading && storeAds && (
          <div className="space-y-3">
            {selectedCards.map(ad => (
              <AdvertisementBulkSelectableCard
                key={ad.id}
                storeAd={ad}
                isSelected
                onToggle={toggleAdSelection}
              />
            ))}

            {unselectedCards.map(ad => (
              <AdvertisementBulkSelectableCard
                key={ad.id}
                storeAd={ad}
                isSelected={false}
                onToggle={toggleAdSelection}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-4 px-6 py-4 bg-white border-t">
        <Button
          variant="outline"
          disabled={isBulkEditDisabled}
          className="w-full disabled:border-gray-500 disabled:opacity-30"
          onClick={() =>
            navigate('/preview-and-edit-ad-price', {
              state: {
                selectedAdsData,
                percentage: Number(percentage),
              },
            })
          }
        >
          پیش نمایش
        </Button>

        <Button
          disabled={isBulkEditDisabled}
          className="w-full disabled:border-gray-500 disabled:opacity-30"
          onClick={applyBulkEdit}
        >
          {isBulkEditing ? <SpinnerLoading size={24} /> : 'ثبت'}
        </Button>
      </div>

      <FilterAdsByInputGroupBottomSheet
        shouldOpen={openFilterBottomSheet}
        setShouldOpen={setOpenFilterBottomSheet}
        filterAdType={filterAdType}
        setFilterAdType={setFilterAdType}
      />
    </div>
  );
};

export default EditBulkStoreAdsPage;
